import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { InputWrapper } from "@amzn/stencil-react-components/input";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { Input } from "@amzn/uxm-stencil-react-components/lib/src/stencil/input";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC } from "react";

import { useUseCaseMetadata } from "../hooks/useUseCaseMetadata";
import { STEP_VALIDATORS } from "./form-validation";
import { FormField } from "./FormField";
import { StepActions } from "./StepActions";
import { StepProps } from "./StepProps";

const OTHER_OPTION = "OTHER";

export const AudienceAndImpactStep: FC<StepProps> = ({ formData, onChange, onNext, onBack, onSaveDraft }) => {
  const { audiences, difficultyLevels, keyImprovementsOptions } = useUseCaseMetadata();
  const hasOtherSelected = formData.impactIndicators.includes(OTHER_OPTION);
  const isValid = STEP_VALIDATORS.audienceAndImpact(formData);

  return (
    <Col gridGap="S400">
      <Text fontSize="T400" fontWeight="bold">
        {getString("useCases.contributeForm.audienceAndImpact")}
      </Text>

      <FormField
        config={{
          id: "target-audience",
          name: "targetAudience",
          type: "multiSelect",
          labelKey: "useCases.contributeForm.labelTargetAudience",
          placeholderKey: "useCases.contributeForm.targetAudiencePlaceholder",
          required: true,
          options: audiences,
        }}
        value={formData.targetAudience}
        onChange={(v) => onChange({ targetAudience: v })}
      />

      <FormField
        config={{
          id: "difficulty-level",
          name: "difficultyLevel",
          type: "select",
          labelKey: "useCases.contributeForm.labelDifficultyLevel",
          placeholderKey: "useCases.contributeForm.difficultyPlaceholder",
          required: true,
          options: difficultyLevels,
        }}
        value={formData.difficultyLevel}
        onChange={(v) => onChange({ difficultyLevel: v })}
      />

      <FormField
        config={{
          id: "impact-indicator",
          name: "impactIndicators",
          type: "multiSelect",
          labelKey: "useCases.contributeForm.labelImpactIndicator",
          placeholderKey: "useCases.contributeForm.impactIndicatorPlaceholder",
          required: true,
          options: keyImprovementsOptions,
          footerKey: "useCases.contributeForm.impactIndicatorFooter",
        }}
        value={formData.impactIndicators}
        onChange={(v) => onChange({ impactIndicators: v })}
      />

      {hasOtherSelected && (
        <InputWrapper
          labelText={getString("useCases.contributeForm.labelOtherImpactIndicator")}
          id="other-impact-indicator"
          required
        >
          {(inputProps) => (
            <Input
              {...inputProps}
              touchpoint="use-case-other-impact"
              value={formData.otherImpactIndicator}
              onChange={(e: any) => onChange({ otherImpactIndicator: e.target.value })}
              placeholder={getString("useCases.contributeForm.otherImpactPlaceholder")}
            />
          )}
        </InputWrapper>
      )}

      {/* Prerequisites — dynamic list */}
      <Col gridGap="S200">
        <Text fontWeight="bold">{getString("useCases.contributeForm.labelPrerequisites")}</Text>
        {formData.prerequisites.map((item: string, index: number) => (
          <Row key={index} gridGap="S200" alignItems="center">
            <Col flex={1}>
              <InputWrapper id={`prerequisite-${index}`} labelText="">
                {(inputProps) => (
                  <Input
                    {...inputProps}
                    touchpoint={`use-case-prerequisite-${index}`}
                    value={item}
                    onChange={(e: any) => {
                      const updated = [...formData.prerequisites];
                      updated[index] = e.target.value;
                      onChange({ prerequisites: updated });
                    }}
                    placeholder={getString("useCases.contributeForm.prerequisitesPlaceholder")}
                    maxLength={200}
                  />
                )}
              </InputWrapper>
            </Col>
            {formData.prerequisites.length > 1 && (
              <Button
                variant={ButtonVariant.Tertiary}
                onClick={() =>
                  onChange({ prerequisites: formData.prerequisites.filter((_: any, i: number) => i !== index) })
                }
                touchpoint={`use-case-remove-prerequisite-${index}`}
              >
                ✕
              </Button>
            )}
          </Row>
        ))}
        {formData.prerequisites.length < 20 && (
          <Button
            variant={ButtonVariant.Tertiary}
            onClick={() => onChange({ prerequisites: [...formData.prerequisites, ""] })}
            touchpoint="use-case-add-prerequisite"
          >
            {getString("useCases.contributeForm.addPrerequisite")}
          </Button>
        )}
      </Col>

      <StepActions onBack={onBack} onSaveDraft={onSaveDraft} onNext={onNext} isNextDisabled={!isValid} />
    </Col>
  );
};
